export const isGlobalRecord = (record) => !record?.clinicId;

export const SCOPE_LABEL = { GLOBAL: "Global", FACILITY: "Facility" };

export const SCOPE_DESCRIPTION = {
  GLOBAL: "Global — Managed by DRCFO Admin",
  FACILITY: "Facility — Managed by your Hospital/Clinic",
};

export const scopeLabel = (record) =>
  isGlobalRecord(record) ? SCOPE_LABEL.GLOBAL : SCOPE_LABEL.FACILITY;

export const scopeDescription = (record) =>
  isGlobalRecord(record)
    ? SCOPE_DESCRIPTION.GLOBAL
    : SCOPE_DESCRIPTION.FACILITY;

export const canEditRecord = (record, { isPlatformAdmin = false } = {}) =>
  isPlatformAdmin ? isGlobalRecord(record) : !isGlobalRecord(record);

export const readOnlyReason = (record, { isPlatformAdmin = false } = {}) => {
  if (canEditRecord(record, { isPlatformAdmin })) return "";
  return isPlatformAdmin
    ? "Facility-owned record — manage it from the facility account."
    : "Global record — managed by the DRCFO admin.";
};

const idOf = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id ? String(value._id) : "";
  return String(value);
};

export const normaliseParameterName = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");

export const parameterMatchKey = (value) =>
  normaliseParameterName(value).toLowerCase();

export const mappingKey = (mapping) =>
  `${idOf(mapping?.test)}::${parameterMatchKey(mapping?.parameterName)}`;

export const findDuplicateMappingIndex = (mappings) => {
  const seen = new Set();
  for (let index = 0; index < (mappings || []).length; index += 1) {
    const mapping = mappings[index];
    if (!mapping?.test) continue;
    const key = mappingKey(mapping);
    if (seen.has(key)) return index;
    seen.add(key);
  }
  return -1;
};

export const WHOLE_TEST_LABEL = "All parameters";

export const describeMapping = (mapping, testNameById = {}) => {
  const testId = idOf(mapping?.test);
  const testName =
    (mapping?.test && typeof mapping.test === "object" && mapping.test.name) ||
    testNameById[testId] ||
    "Unknown test";
  const parameter = normaliseParameterName(mapping?.parameterName);
  return `${testName} · ${parameter || WHOLE_TEST_LABEL}`;
};

export const summariseMappings = (mappings, testNameById = {}, max = 2) => {
  const list = Array.isArray(mappings) ? mappings : [];
  if (!list.length) return { shown: [], extra: 0 };
  return {
    shown: list.slice(0, max).map((m) => describeMapping(m, testNameById)),
    extra: Math.max(0, list.length - max),
  };
};

export const defaultMappingCount = (mappings) =>
  (Array.isArray(mappings) ? mappings : []).filter((m) => m?.isDefault).length;


export const truncateFormula = (formula, max = 42) => {
  const text = String(formula ?? "").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

export const findDuplicateInputKeyIndex = (inputs) => {
  const seen = new Set();
  for (let index = 0; index < (inputs || []).length; index += 1) {
    const key = String(inputs[index]?.key ?? "")
      .trim()
      .toLowerCase();
    if (!key) continue;
    if (seen.has(key)) return index;
    seen.add(key);
  }
  return -1;
};

export const buildPreviewFields = (inputs) =>
  (Array.isArray(inputs) ? inputs : [])
    .map((input) => ({
      key: String(input?.key ?? "").trim(),
      parameterName: normaliseParameterName(input?.parameterName),
    }))
    .filter((input) => input.key);

export const PREVIEW_STATUS_TEXT = {
  OK: "Calculated",
  MISSING_INPUTS: "Waiting for required inputs",
  DIVISION_BY_ZERO: "Cannot calculate (division by zero)",
  INVALID: "Cannot calculate with these values",
};

export const describePreview = (outcome) => {
  if (!outcome) return { tone: "idle", text: "" };
  const status = outcome.status || "INVALID";
  if (status === "OK") {
    return {
      tone: "success",
      text: PREVIEW_STATUS_TEXT.OK,
      value: outcome.display ?? outcome.value ?? "",
    };
  }
  const missing = Array.isArray(outcome.missing) ? outcome.missing : [];
  return {
    tone: status === "MISSING_INPUTS" ? "warning" : "error",
    text:
      outcome.reason ||
      PREVIEW_STATUS_TEXT[status] ||
      PREVIEW_STATUS_TEXT.INVALID,
    missing,
  };
};

export const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "true", label: "Active only" },
];

export const SCOPE_FILTERS = [
  { value: "", label: "All scopes" },
  { value: "global", label: "Global" },
  { value: "hospital", label: "Facility" },
];
